import { useState, type ChangeEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Baby, HeartPulse, NotebookPen, RotateCcw, Stethoscope, type LucideIcon } from 'lucide-react';
import { VITALS_RECHECK_REASON_MAX, VITALS_RECHECK_REASON_MIN, vitalFlag, vitalsProblems, type FlaggedVital, type VitalFlag } from '@his/shared';
import { ApiError, post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { useTranslation } from '@/lib/i18n';
import { tr } from '@/lib/i18n-core';
import { cn } from '@/lib/utils';
import { Dialog } from '@/components/ui/layout';
import { Alert, Button, Field, Input, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { painFlag, VITAL_STYLE } from '@/features/opd/emr/vitals';
import { birthWeightText, gestationText, type NewbornFacts } from '@/features/opd/emr/completion';

function BirthFacts({ facts }: { facts: NewbornFacts }) {
  const weight = birthWeightText(facts.birthWeightGrams);
  const gestation = gestationText(facts.gestationalAgeWeeks, facts.gestationalAgeDays);
  return (
    <div className="col-span-2 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border border-dashed border-info/40 bg-info/[0.06] px-3 py-2 text-sm sm:col-span-3">
      <span className="inline-flex items-center gap-1.5 font-medium text-info">
        <Baby className="h-4 w-4" aria-hidden /> {tr('opd', 'emr_at_birth')}
      </span>
      <span className="tabular">{weight ?? tr('opd', 'emr_birth_weight_unknown')}</span>
      <span className="tabular">{gestation ?? tr('opd', 'emr_gestation_unknown')}</span>
      {(!weight || !gestation) && (
        <Link to={`/patients/${facts.patientId}`} className="text-xs font-medium text-primary underline underline-offset-2">
          {tr('opd', 'emr_birth_details_missing')}
        </Link>
      )}
    </div>
  );
}

/**
 * Records a set of vitals against whichever episode the caller is in the
 * middle of — an OPD appointment, an encounter, or an IPD admission. One form
 * for all three, because the reading itself (BP, pulse, temperature…) never
 * changes shape; only what it gets attached to does.
 */
export interface VitalsDialogProps {
  open: boolean;
  onClose: () => void;
  patientId: string;
  patientName?: string;
  appointmentId?: string;
  encounterId?: string;
  admissionId?: string;
  /** Query keys to invalidate on success, e.g. ['queue'] or ['admission', id]. */
  invalidateKeys?: string[][];
  recheck?: boolean;
  newborn?: NewbornFacts | null;
}

const EMPTY = {
  bpSystolic: '',
  bpDiastolic: '',
  pulse: '',
  respiratoryRate: '',
  temperatureC: '',
  spo2: '',
  heightCm: '',
  weightKg: '',
  painScore: '',
  bloodSugarMgDl: '',
  headCircumferenceCm: '',
  notes: '',
};

type VitalsForm = typeof EMPTY;
type VitalsKey = keyof VitalsForm;

const FIRST_FIELD_ID = 'vitals-bp-systolic';
const RECHECK_REASON_ID = 'vitals-recheck-reason';
const PAIN_SCORES = Array.from({ length: 11 }, (_, score) => score);

const RANGE_CHECKED: Partial<Record<VitalsKey, FlaggedVital>> = {
  bpSystolic: 'bpSystolic',
  bpDiastolic: 'bpDiastolic',
  pulse: 'pulse',
  respiratoryRate: 'respiratoryRate',
  temperatureC: 'temperatureC',
  spo2: 'spo2',
  bloodSugarMgDl: 'bloodSugarMgDl',
};

const PROBLEM_FIELD_ID: Partial<Record<VitalsKey, string>> = {
  bpSystolic: 'vitals-bp-systolic',
  bpDiastolic: 'vitals-bp-diastolic',
  weightKg: 'vitals-weight',
};

function reading(value: string): number | undefined {
  return value.trim() === '' ? undefined : Number(value);
}

function problemText(path: string, form: VitalsForm): string {
  if (path === 'weightKg') {
    const height = reading(form.heightCm) ?? 0;
    const weight = reading(form.weightKg) ?? 0;
    const bmi = height > 0 ? Math.round(weight / (height / 100) ** 2) : 0;
    return tr('opd', 'vitals_height_weight_mismatch', { bmi });
  }
  const paired = (reading(form.bpSystolic) === undefined) === (reading(form.bpDiastolic) === undefined);
  return tr('opd', paired ? 'vitals_bp_order' : 'vitals_bp_pair');
}

function rangeFlag(key: VitalsKey, value: string): VitalFlag | null {
  const checked = RANGE_CHECKED[key];
  return checked ? vitalFlag(checked, reading(value)) : null;
}

function bodyMassIndex(form: VitalsForm): string | null {
  const height = reading(form.heightCm) ?? 0;
  const weight = reading(form.weightKg) ?? 0;
  if (height <= 0 || weight <= 0) return null;
  return (weight / (height / 100) ** 2).toFixed(1);
}

function painTone(score: number): string {
  if (painFlag(score)) return 'border-destructive bg-destructive text-destructive-foreground';
  if (score >= 4) return 'border-warning bg-warning text-warning-foreground';
  return 'border-success bg-success text-success-foreground';
}

function recheckRefused(error: unknown): boolean {
  return error instanceof ApiError && error.details.some((detail) => detail.path === 'recheckReason');
}

function VitalGroup({
  title,
  icon: Icon,
  tone,
  columns = 1,
  className,
  children,
}: {
  title: string;
  icon: LucideIcon;
  tone: string;
  columns?: 1 | 2 | 3;
  className?: string;
  children: ReactNode;
}) {
  return (
    <section className={cn('rounded-xl border bg-card', className)}>
      <header className="flex items-center gap-2.5 border-b px-4 py-2.5">
        <span className={cn('grid h-7 w-7 shrink-0 place-items-center rounded-lg', tone)} aria-hidden>
          <Icon className="h-3.5 w-3.5" />
        </span>
        <h3 className="text-sm font-semibold">{title}</h3>
      </header>
      <div className={cn('grid gap-4 p-4', columns === 2 && 'grid-cols-2', columns === 3 && 'grid-cols-2 sm:grid-cols-3')}>{children}</div>
    </section>
  );
}

function RangeChip({ flag }: { flag: VitalFlag }) {
  const Icon = flag === 'low' ? ArrowDown : ArrowUp;
  return (
    <span
      className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-destructive/10 px-2 py-0.5 text-[11px] font-semibold text-destructive"
      title={tr('opd', flag === 'low' ? 'vital_below_range' : 'vital_above_range')}
    >
      <Icon className="h-3 w-3" aria-hidden />
      {tr('opd', flag === 'low' ? 'vitals_flag_low' : 'vitals_flag_high')}
    </span>
  );
}

function Measure({
  id,
  label,
  value,
  onChange,
  error,
  flag = null,
  step,
  min,
  max,
  placeholder,
  hint,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  error?: string;
  flag?: VitalFlag | null;
  step?: string;
  min?: number;
  max?: number;
  placeholder?: string;
  hint?: string;
}) {
  const described = error ? `${id}-error` : hint ? `${id}-hint` : undefined;
  return (
    <Field label={label} htmlFor={id} error={error} hint={hint}>
      <div className="flex items-center gap-2">
        <Input
          id={id}
          type="number"
          inputMode={step ? 'decimal' : 'numeric'}
          step={step}
          min={min}
          max={max}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          aria-invalid={error ? true : undefined}
          aria-describedby={described}
          className={cn('min-w-0 flex-1 tabular', flag && !error && 'border-destructive/50 bg-destructive/5')}
        />
        {flag && <RangeChip flag={flag} />}
      </div>
    </Field>
  );
}

function BmiReadout({ value }: { value: string | null }) {
  const Icon = VITAL_STYLE.bmi.icon;
  return (
    <div className="space-y-1.5">
      <span id="vitals-bmi-label" className="flex min-h-[1.125rem] items-center text-sm font-medium leading-none">
        {tr('opd', 'vital_bmi')}
      </span>
      <output
        aria-labelledby="vitals-bmi-label"
        aria-live="polite"
        className="flex h-9 items-center gap-2 rounded-md border border-dashed bg-muted/40 px-3 text-sm font-semibold tabular"
      >
        <span className={cn('grid h-5 w-5 shrink-0 place-items-center rounded', VITAL_STYLE.bmi.tone)} aria-hidden>
          <Icon className="h-3 w-3" />
        </span>
        {value ?? <span className="truncate text-xs font-normal text-muted-foreground">{tr('opd', 'vitals_bmi_from_height_weight')}</span>}
      </output>
    </div>
  );
}

function PainScale({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const selected = value === '' ? null : Number(value);
  return (
    <div className="col-span-2 space-y-1.5">
      <span id="vitals-pain-label" className="flex min-h-[1.125rem] items-center text-sm font-medium leading-none">
        {tr('opd', 'pain_score_0_10')}
      </span>
      <div role="group" aria-labelledby="vitals-pain-label" className="grid grid-cols-6 gap-1.5 sm:grid-cols-11">
        {PAIN_SCORES.map((score) => (
          <button
            key={score}
            type="button"
            aria-pressed={selected === score}
            onClick={() => onChange(selected === score ? '' : String(score))}
            className={cn(
              'h-9 rounded-lg border text-sm font-semibold tabular transition-colors focus-ring',
              selected === score ? painTone(score) : 'bg-card text-foreground/80 hover:bg-muted',
            )}
          >
            {score}
          </button>
        ))}
      </div>
    </div>
  );
}

export function VitalsDialog({
  open,
  onClose,
  patientId,
  patientName,
  appointmentId,
  encounterId,
  admissionId,
  invalidateKeys = [],
  recheck = false,
  newborn = null,
}: VitalsDialogProps) {
  const toast = useToast();
  const common = useTranslation('common');
  const queryClient = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [attempted, setAttempted] = useState(false);
  const [recheckMode, setRecheckMode] = useState(recheck);
  const [reason, setReason] = useState('');

  const num = reading;
  const focusField = (id: string) => window.requestAnimationFrame(() => document.getElementById(id)?.focus());

  const save = useMutation({
    mutationFn: () =>
      post('/vitals', {
        patientId,
        appointmentId,
        encounterId,
        admissionId,
        bpSystolic: num(form.bpSystolic),
        bpDiastolic: num(form.bpDiastolic),
        pulse: num(form.pulse),
        respiratoryRate: num(form.respiratoryRate),
        temperatureC: num(form.temperatureC),
        spo2: num(form.spo2),
        heightCm: num(form.heightCm),
        weightKg: num(form.weightKg),
        painScore: num(form.painScore),
        bloodSugarMgDl: num(form.bloodSugarMgDl),
        headCircumferenceCm: num(form.headCircumferenceCm),
        notes: form.notes.trim() || undefined,
        recheckReason: recheckMode ? reason.trim() : undefined,
      }),
    onSuccess: () => {
      toast.success(tr('opd', recheckMode ? 'vitals_recheck_recorded' : 'vitals_recorded'));
      setForm(EMPTY);
      setReason('');
      setAttempted(false);
      for (const key of invalidateKeys) void queryClient.invalidateQueries({ queryKey: key });
      onClose();
    },
    onError: (error) => {
      if (recheckRefused(error)) {
        setRecheckMode(true);
        setAttempted(true);
        focusField(RECHECK_REASON_ID);
        return;
      }
      toast.error(tr('opd', 'vitals_record_failed'), describeError(error));
    },
  });

  const set = (key: VitalsKey) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm({ ...form, [key]: e.target.value });
  const setValue = (key: VitalsKey) => (value: string) => setForm({ ...form, [key]: value });

  const close = () => {
    setAttempted(false);
    onClose();
  };

  // At least one reading, so an empty submit can't create a blank row.
  const hasAnyReading = Object.entries(form).some(([k, v]) => k !== 'notes' && v.trim() !== '');
  const problems = vitalsProblems({
    bpSystolic: num(form.bpSystolic) ?? null,
    bpDiastolic: num(form.bpDiastolic) ?? null,
    heightCm: num(form.heightCm) ?? null,
    weightKg: num(form.weightKg) ?? null,
  });

  const errors: Partial<Record<VitalsKey, string>> = {};
  if (attempted) {
    for (const problem of problems) errors[problem.path as VitalsKey] ??= problemText(problem.path, form);
  }
  const reasonMissing = recheckMode && reason.trim().length < VITALS_RECHECK_REASON_MIN;
  const reasonError = attempted && reasonMissing ? tr('opd', 'vitals_recheck_reason_required') : undefined;
  const warning = !attempted
    ? null
    : !hasAnyReading
      ? tr('opd', 'vitals_enter_a_reading')
      : problems.length > 0 || reasonMissing
        ? common.t('form.fix_marked')
        : null;

  const submit = () => {
    if (hasAnyReading && problems.length === 0 && !reasonMissing) {
      save.mutate();
      return;
    }
    setAttempted(true);
    const target = reasonMissing ? RECHECK_REASON_ID : hasAnyReading ? PROBLEM_FIELD_ID[problems[0]?.path as VitalsKey] : FIRST_FIELD_ID;
    if (target) document.getElementById(target)?.focus();
  };

  const measure = (key: VitalsKey, id: string, label: string, extra: { step?: string; min?: number; max?: number; placeholder?: string; hint?: string } = {}) => (
    <Measure id={id} label={label} value={form[key]} onChange={set(key)} error={errors[key]} flag={rangeFlag(key, form[key])} {...extra} />
  );

  return (
    <Dialog
      open={open}
      onClose={close}
      wide
      sheetOnPhone
      icon={recheckMode ? RotateCcw : HeartPulse}
      dirty={Object.values(form).some((value) => value.trim() !== '') || reason.trim() !== ''}
      title={tr('opd', recheckMode ? 'vitals_recheck' : 'record_vitals')}
      description={patientName}
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            {common.t('action.cancel')}
          </Button>
          <Button onClick={submit} loading={save.isPending}>
            {tr('opd', recheckMode ? 'vitals_save_recheck' : 'vitals_save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {warning && <Alert variant="warning">{warning}</Alert>}

        {recheckMode && (
          <div className="flex items-start gap-3 rounded-xl border border-warning/40 bg-warning/10 p-4">
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-warning/15 text-warning" aria-hidden>
              <RotateCcw className="h-4 w-4" />
            </span>
            <div className="min-w-0 flex-1 space-y-3">
              <div>
                <p className="text-sm font-semibold">{tr('opd', 'vitals_recheck_title')}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">{tr('opd', 'vitals_recheck_body')}</p>
              </div>
              <Field label={tr('opd', 'vitals_recheck_reason')} htmlFor={RECHECK_REASON_ID} required error={reasonError}>
                <Input
                  id={RECHECK_REASON_ID}
                  value={reason}
                  maxLength={VITALS_RECHECK_REASON_MAX}
                  onChange={(event) => setReason(event.target.value)}
                  placeholder={tr('opd', 'vitals_recheck_reason_placeholder')}
                  aria-invalid={reasonError ? true : undefined}
                  aria-describedby={reasonError ? `${RECHECK_REASON_ID}-error` : undefined}
                />
              </Field>
            </div>
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-2">
          <VitalGroup title={tr('opd', 'vitals_group_circulation')} {...VITAL_STYLE.bp} columns={3} className="md:col-span-2">
            {measure('bpSystolic', 'vitals-bp-systolic', tr('opd', 'vitals_bp_systolic'), { placeholder: '120' })}
            {measure('bpDiastolic', 'vitals-bp-diastolic', tr('opd', 'vitals_bp_diastolic'), { placeholder: '80' })}
            {measure('pulse', 'vitals-pulse', tr('opd', 'vitals_pulse_per_min'))}
          </VitalGroup>

          <VitalGroup title={tr('opd', 'vitals_group_respiration')} {...VITAL_STYLE.respiratoryRate} columns={2}>
            {measure('respiratoryRate', 'vitals-resp-rate', tr('opd', 'vitals_resp_rate_per_min'))}
            {measure('spo2', 'vitals-spo2', tr('opd', 'vitals_spo2_pct'))}
          </VitalGroup>

          <VitalGroup title={tr('opd', 'vital_temperature')} {...VITAL_STYLE.temperature}>
            {measure('temperatureC', 'vitals-temperature', tr('opd', 'vitals_temperature_c'), { step: '0.1' })}
          </VitalGroup>

          <VitalGroup title={tr('opd', 'vitals_group_body')} {...VITAL_STYLE.weight} columns={3} className="md:col-span-2">
            {measure('heightCm', 'vitals-height', tr('opd', 'vitals_height_cm'), { step: '0.1' })}
            {measure('weightKg', 'vitals-weight', tr('opd', 'vitals_weight_kg'), { step: '0.1' })}
            <BmiReadout value={bodyMassIndex(form)} />
            {newborn && <BirthFacts facts={newborn} />}
          </VitalGroup>

          <VitalGroup title={tr('opd', 'vitals_group_other')} icon={Stethoscope} tone="bg-primary/10 text-primary" columns={2} className="md:col-span-2">
            <PainScale value={form.painScore} onChange={setValue('painScore')} />
            {measure('bloodSugarMgDl', 'vitals-sugar', tr('opd', 'vitals_blood_sugar_mg_dl'))}
            {measure('headCircumferenceCm', 'vitals-head', tr('opd', 'vitals_head_circumference_cm'), {
              step: '0.1',
              min: 20,
              max: 70,
              hint: tr('opd', 'vitals_head_circumference_hint'),
            })}
          </VitalGroup>

          <VitalGroup title={common.t('field.notes')} icon={NotebookPen} tone="bg-muted text-muted-foreground" className="md:col-span-2">
            <Textarea id="vitals-notes" rows={2} aria-label={common.t('field.notes')} value={form.notes} onChange={set('notes')} />
          </VitalGroup>
        </div>
      </div>
    </Dialog>
  );
}
